/**
 * Tracker links: turn whatever a person pastes from Linear or Jira into the
 * thing it names, before any API call.
 *
 * The rule of the paste box is "put a link in and we figure it out". This
 * module is the figuring-out that needs no network: it reads the URL shape (or
 * a bare key) and says which tracker it belongs to and what kind of object it
 * points at. The server then resolves the object against the tracker's API to
 * confirm it exists and to fetch its human name.
 *
 * Shapes recognised:
 *
 *   Linear
 *     https://linear.app/<ws>/team/<KEY>[/...]              → team by key
 *     https://linear.app/<ws>/project/<slug>-<slugId>[/...] → project by slug id
 *     https://linear.app/<ws>/view/<slug>-<slugId>[/...]    → saved view by slug id
 *     https://linear.app/<ws>/issue/<KEY>-<n>[/...]          → issue by identifier
 *   Jira
 *     https://<site>/browse/<KEY>-<n>[?jql=...|?filter=...]  → issue by key
 *     https://<site>/jira/software/projects/<KEY>/issues/<KEY>-<n> → issue by key
 *     https://<site>/jira/(software|core)/(c/)?projects/<KEY>/... → project by key
 *     https://<site>/projects/<KEY>[/...]                    → project by key
 *     https://<site>/...?jql=<query>                         → query (jql wins over filter)
 *     https://<site>/...?filter=<id>                         → query by saved filter id
 *     https://<site>/...?selectedIssue=<KEY>-<n>             → issue by key
 *   Bare text
 *     KEY-12                                                 → issue (tracker unknown)
 *     KEY                                                    → project or team key (tracker unknown)
 *     labels = x AND project = DCP ORDER BY created DESC     → Jira query (see looksLikeJql)
 *
 * Pure and dependency-free so the browser can pre-classify a paste (to say
 * "that looks like a Linear project" before the round trip) and the server can
 * run the same parse before touching a token.
 */
// Jira project keys may contain underscores (MY_PROJ-12); Linear's never do.
const ISSUE_ID = /^([A-Za-z][A-Za-z0-9_]*)-(\d+)$/;
const PLAIN_KEY = /^[A-Za-z][A-Za-z0-9_]{0,15}$/;
function trimSlashes(path) {
    return path.split("/").filter((segment) => segment.length > 0);
}
function parseLinearPath(segments) {
    // linear.app/<workspace>/<kind>/<ref>/...
    if (segments.length < 3)
        return null;
    const [workspaceSegment, kind, ref] = segments;
    const workspace = safeDecode(workspaceSegment ?? "");
    if (!kind || !ref)
        return null;
    const decoded = safeDecode(ref);
    if (kind === "team" && PLAIN_KEY.test(decoded)) {
        return { source: "linear", kind: "team", key: decoded.toUpperCase(), workspace };
    }
    if (kind === "project" || kind === "view") {
        // The slug id is the trailing token; Linear's `project(id:)` and
        // `customView(id:)` both accept it.
        const lastDash = decoded.lastIndexOf("-");
        const isUuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(decoded);
        const slugId = !isUuid && lastDash >= 0 ? decoded.slice(lastDash + 1) : decoded;
        if (!slugId)
            return null;
        return { source: "linear", kind, slugId, slug: decoded, workspace };
    }
    if (kind === "issue" && ISSUE_ID.test(decoded)) {
        return { source: "linear", kind: "issue", identifier: decoded.toUpperCase(), workspace };
    }
    return null;
}
/** Jira's saved filter ids are positive integers; `-1` and `allissues` are system lists. */
const JIRA_FILTER_ID = /^[1-9]\d{0,17}$/;
/** The project key a Jira path sits under (`/projects/KEY/...`), if any. */
function jiraProjectKeyIn(segments) {
    const projectsAt = segments.indexOf("projects");
    const key = projectsAt >= 0 ? segments[projectsAt + 1] : undefined;
    return key && PLAIN_KEY.test(key) ? key.toUpperCase() : null;
}
/** The issue key a Jira navigator path names (`/issues/KEY-12`), if any. */
function jiraIssueSegmentIn(segments) {
    const issuesAt = segments.indexOf("issues");
    const segment = issuesAt >= 0 ? segments[issuesAt + 1] : undefined;
    return segment && ISSUE_ID.test(segment) ? segment.toUpperCase() : null;
}
/**
 * A `jql` or `filter` param names a selection Jira owns, so it is read before
 * the path: `/projects/DCP/issues/?jql=...` is that query, never all of DCP.
 * The jql wins over a filter because it is what is on screen. A system filter
 * (`-1`, `allissues`) on a project path is that project's list, so it falls
 * through; anywhere else it is a query naming nothing we can read back.
 */
function parseJiraQuery(url, segments) {
    const jql = url.searchParams.get("jql")?.trim() || null;
    const filter = url.searchParams.get("filter")?.trim() || null;
    if (!jql && !filter)
        return null;
    const filterId = !jql && filter && JIRA_FILTER_ID.test(filter) ? filter : null;
    if (!jql && !filterId && jiraProjectKeyIn(segments))
        return null;
    // The ticket open beside the list, so a paste meant as one ticket still is.
    const selected = url.searchParams.get("selectedIssue");
    const selectedIssueKey = selected && ISSUE_ID.test(selected) ? selected.toUpperCase() : jiraIssueSegmentIn(segments);
    return { source: "jira", kind: "query", filterId, jql, selectedIssueKey };
}
function parseJiraPath(url) {
    const segments = trimSlashes(url.pathname);
    // /browse/KEY-12 is one ticket's own page, even when Jira carries along the
    // list it was opened from (`?filter=...`, `?jql=...`).
    if (segments[0] === "browse" && segments[1] && ISSUE_ID.test(segments[1])) {
        return { source: "jira", kind: "issue", key: segments[1].toUpperCase() };
    }
    const query = parseJiraQuery(url, segments);
    if (query)
        return query;
    const selectedIssue = url.searchParams.get("selectedIssue");
    if (selectedIssue && ISSUE_ID.test(selectedIssue)) {
        return { source: "jira", kind: "issue", key: selectedIssue.toUpperCase() };
    }
    // The issue navigator, /jira/software/projects/KEY/issues/KEY-12, names an
    // issue even though it sits under a project path.
    const issueKey = jiraIssueSegmentIn(segments);
    if (issueKey)
        return { source: "jira", kind: "issue", key: issueKey };
    // /jira/software/projects/KEY/..., /jira/software/c/projects/KEY/...,
    // /jira/core/projects/KEY/..., /projects/KEY/...
    const projectKey = jiraProjectKeyIn(segments);
    if (projectKey)
        return { source: "jira", kind: "project", key: projectKey };
    return null;
}
function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        return value;
    }
}
/**
 * Classify a pasted link or key. Returns null for text that names nothing a
 * Team can attach to (an initiative page, a search URL, a sentence).
 */
export function parseTrackerLink(input, options = {}) {
    const text = input.trim();
    if (!text)
        return null;
    let url = null;
    if (/^https?:\/\//i.test(text)) {
        try {
            url = new URL(text);
        }
        catch {
            url = null;
        }
    }
    else if (/^[a-z0-9.-]+\.(atlassian\.net|linear\.app)\//i.test(text) || /^linear\.app\//i.test(text)) {
        try {
            url = new URL(`https://${text}`);
        }
        catch {
            url = null;
        }
    }
    if (url) {
        const host = url.hostname.toLowerCase();
        if (host === "linear.app" || host.endsWith(".linear.app")) {
            return parseLinearPath(trimSlashes(url.pathname));
        }
        if (host.endsWith(".atlassian.net") ||
            host.endsWith(".jira.com") ||
            (options.jiraHosts ?? []).some((known) => known.toLowerCase() === host)) {
            return parseJiraPath(url);
        }
        return null;
    }
    if (ISSUE_ID.test(text)) {
        return { source: null, kind: "issue", identifier: text.toUpperCase() };
    }
    if (PLAIN_KEY.test(text)) {
        return { source: null, kind: "key", key: text.toUpperCase() };
    }
    // Only Jira takes a query, so pasted JQL names its tracker by itself.
    if (looksLikeJql(text)) {
        return { source: "jira", kind: "query", filterId: null, jql: collapseJql(text), selectedIssueKey: null };
    }
    return null;
}
/**
 * Whether bare pasted text reads as JQL rather than a name or a sentence: no
 * URL, more than one word, and a clause JQL would take (`field = value`,
 * `field in (...)`, `field is EMPTY`, `ORDER BY field`) outside any quoted
 * literal. Consulted only after the link and key parses, so `DCP-12` never is.
 */
export function looksLikeJql(input) {
    const text = input.trim();
    if (text.includes("://") || !/\s/.test(text))
        return false;
    const bare = stripJqlLiterals(text);
    return (/[\w"\]]\s*(?:!=|!~|>=|<=|=|~|>|<)\s*\S/.test(bare) ||
        /\b\w+\s+(?:not\s+)?in\s*\(/i.test(bare) ||
        /\b\w+\s+is\s+(?:not\s+)?(?:empty|null)\b/i.test(bare) ||
        /\border\s+by\s+\w/i.test(bare));
}
/** JQL with its quoted literals emptied, so words inside a value never read as syntax. */
export function stripJqlLiterals(jql) {
    return jql.replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""');
}
/** JQL on one line: whitespace runs collapse to one space. Otherwise verbatim. */
export function collapseJql(jql) {
    return jql.replace(/\s+/g, " ").trim();
}
/** The longest JQL a Team attaches as a query; past it, a saved filter. */
export const JIRA_QUERY_JQL_MAX = 1000;
/**
 * The ref a Jira query attachment stores (`desk_attachments.ref`):
 * `filter:<id>` for a saved filter, whose JQL is read again on every turn so
 * an edit in Jira flows through, or `jql:<query>` on one line.
 */
export function formatJiraQueryRef(query) {
    return "filterId" in query ? `filter:${query.filterId}` : `jql:${collapseJql(query.jql)}`;
}
/** Read a stored ref back; null for anything that is not one (a legacy `board-1`, an empty query). */
export function parseJiraQueryRef(ref) {
    const text = ref.trim();
    if (text.startsWith("filter:")) {
        const filterId = text.slice("filter:".length);
        return JIRA_FILTER_ID.test(filterId) ? { filterId } : null;
    }
    if (text.startsWith("jql:")) {
        const jql = collapseJql(text.slice("jql:".length));
        return jql.length > 0 && jql.length <= JIRA_QUERY_JQL_MAX ? { jql } : null;
    }
    return null;
}
/** One line saying what a parse found, for the paste box to echo back. */
export function describeTrackerLink(target) {
    if (target.source === "linear") {
        if (target.kind === "team")
            return `Linear team ${target.key}`;
        if (target.kind === "project")
            return "Linear project";
        if (target.kind === "view")
            return "Linear view";
        return `Linear issue ${target.identifier}`;
    }
    if (target.source === "jira") {
        if (target.kind === "project")
            return `Jira project ${target.key}`;
        if (target.kind === "query")
            return target.filterId ? "Jira filter" : "Jira query";
        return `Jira issue ${target.key}`;
    }
    if (target.kind === "issue")
        return `issue ${target.identifier}`;
    return `key ${target.key}`;
}
/**
 * The ticket a parsed target names (`UPD-12`): an issue's own key, or the
 * issue open beside a Jira filter or query. Null for a project, team, view,
 * or a query with nothing selected.
 */
export function ticketKeyOf(target) {
    if (!target)
        return null;
    if (target.kind === "issue")
        return "key" in target ? target.key : target.identifier;
    if (target.kind === "query")
        return target.selectedIssueKey;
    return null;
}
//# sourceMappingURL=trackerLinks.js.map